import React, { useRef, useState } from "react";
import { Avatar, Badge, Button, Checkbox, Drawer, Flex, Group, Input, Modal, Paper, Text, Tooltip } from "@mantine/core";
import { PageFilter, PageLayout } from "../../../components/layout";
import { IconBrandTelegram, IconCircleCheck, IconCircleMinus, IconEdit, IconEyeCheck, IconInfoSquare, IconPlus, IconSearch } from "@tabler/icons";
import { ColumnType, ITableRef, Table } from "../../../components/table";
import { IProduct } from "../../../interfaces/IProduct";
import { ProductsApi } from "../../../apis";
import { dateSecFormat } from "../../../utils/date";
import noimage from "../../../assets/no-image.png";
import { useSelector } from "react-redux";
import { IGeneral } from "../../../interfaces/IGeneral";
import { IReference } from "../../../models/General";
import { useNavigate } from "react-router";
import { RequestPinForm } from "../../../components/buyer-product/request-pin-form";
import { ActiveForm } from "../../../components/buyer-product/active-form";
import { SupplierProductDetail } from "./detail";
import { useDebouncedValue } from "@mantine/hooks";
import { IAuth } from "../../../interfaces/IAuth";
import { IPermissions } from "../../../interfaces/IPermissions";

export function SupplierProductList() {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumb();
  const ref = useRef<ITableRef>(null);
  const [query, setQuery] = useState("");
  const [queryDebounced] = useDebouncedValue(query, 400);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const { productTypes, productCategories, productRegistrationStatus, productStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const [action, setAction] = React.useState<string[]>([]);
  const [payload, setPayload] = React.useState<string[]>([]);

  const columns = useHeader({
    user,
    productTypes,
    productCategories,
    productRegistrationStatus,
    productStatus,
    onClick: (key, record) => {
      switch (key) {
        case "edit": {
          navigate(`/scf-dcf-product/scf-product/edit/${record.id}/1`);
          break;
        }
        case "detail": {
          setAction(["DETAIL", record as any]);
          break;
        }
        case "active": {
          setPayload(record as any);
          setAction(["ACTIVE"]);
          break;
        }
        case "in_active": {
          setPayload(record as any);
          setAction(["INACTIVE"]);
          break;
        }

        case "is_compolete": {
          setAction(["ISCOMPOLETE", record as any]);
          break;
        }
        case "control": {
          setAction(["DETAIL", record as any, "CONTROL"]);
          break;
        }
      }
    },
  });

  const onCancel = async () => {
    setAction([]);
  };

  const reload = () => {
    ref?.current?.reload();
    setPayload([]);
  };

  return (
    <>
      <PageLayout
        title="Supplier LED SCF"
        subTitle="Supplier LED SCF бүтээгдэхүүний жагсаалт"
        breadcrumb={breadcrumbs}
        extra={[
          <div key={1}>
            {(user?.permissions || []).filter((c) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_SL" && c.isCreate).length > 0 && (
              <Button size="sm" key={0} onClick={() => navigate("/scf-dcf-product/scf-product/new")} variant="outline" leftIcon={<IconPlus />}>
                Бүтээгдэхүүн
              </Button>
            )}
          </div>,
        ]}>
        <Paper radius="sm" p="md" withBorder>
          <PageFilter
            left={[
              <Tooltip label="Бүтээгдэхүүний нэрээр хайх" color="indigo" position="top-end" withArrow key={2}>
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  placeholder="Хайлт хийх"
                  rightSection={<IconSearch size={18} style={{ display: "block", opacity: 0.5 }} />}
                />
              </Tooltip>,
            ]}
          />
          <Table
            ref={ref}
            name="scf-product.list"
            columns={columns}
            filters={{ query: queryDebounced, type: "SUPPLIER_LED" }}
            loadData={(data) => ProductsApi.list(data!)}
          />
        </Paper>
      </PageLayout>
      <Modal opened={action[0] === "ISCOMPOLETE"} onClose={() => setAction([])} withCloseButton={false}>
        <RequestPinForm
          title="Баталгаажуулах пин код"
          subTitle="Мэдээллээ сайтар нягтлан ПИН код оруулна уу."
          action={action}
          onCancel={onCancel}
          reload={reload}
          type="SUPPLIER_LED"
        />
      </Modal>
      <Modal opened={action[0] === "INACTIVE" || action[0] === "ACTIVE"} onClose={() => setAction([])} withCloseButton={false} centered>
        <ActiveForm
          title="Funder систем"
          subTitle="Мэдээллээ сайтар нягтлан ПИН код оруулна уу."
          action={action}
          onCancel={onCancel}
          payload={payload}
          reload={reload}
        />
      </Modal>
      <Drawer
        opened={action[0] === "DETAIL"}
        onClose={() => setAction([])}
        withCloseButton={false}
        position="right"
        size="80%"
        styles={{ drawer: { overflowY: "scroll" } }}>
        <SupplierProductDetail action={action} reload={reload} onCancel={onCancel} />
      </Drawer>
    </>
  );
}

const useHeader = ({
  user,
  onClick,
  productTypes,
  productCategories,
  productRegistrationStatus,
  productStatus,
}: {
  user: any;
  onClick: (key: string, record: IProduct) => void;
  productTypes: IReference[];
  productCategories: IReference[];
  productRegistrationStatus: IReference[];
  productStatus: IReference[];
}): ColumnType<IProduct>[] => [
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap="sm">
          {(user?.permissions || []).filter((c: IPermissions) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_STATUS_CHANGE_SL").length > 0 &&
            record.registrationStatus === "REVIEWED" &&
            record.productStatus === "INACTIVE" && (
              <Tooltip label="Идэвхтэй болгох" position="bottom">
                <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("active", record)}>
                  <IconCircleCheck />
                </Button>
              </Tooltip>
            )}
          {(user?.permissions || []).filter((c: IPermissions) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_STATUS_CHANGE_SL").length > 0 &&
            record.registrationStatus === "REVIEWED" &&
            record.productStatus === "ACTIVE" && (
              <Tooltip label="Идэвхгүй болгох" position="bottom">
                <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("in_active", record)}>
                  <IconCircleMinus />
                </Button>
              </Tooltip>
            )}
          {(user?.permissions || []).filter((c: IPermissions) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_SL" && c.isEdit).length > 0 &&
            record.isCompleted &&
            !record.isSent && (
              <Tooltip label="Хяналтад илгээх" position="bottom">
                <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("is_compolete", record)}>
                  <IconBrandTelegram />
                </Button>
              </Tooltip>
            )}
          {(user?.permissions || []).filter((c: IPermissions) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_SL" && c.isEdit).length > 0 &&
            record.registrationStatus === "DRAFT" && (
              <Tooltip label="Засварлах" position="bottom">
                <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("edit", record)}>
                  <IconEdit />
                </Button>
              </Tooltip>
            )}

          {(user?.permissions || []).filter((c: IPermissions) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_SL" && c.isView).length > 0 && (
            <Tooltip label="Дэлгэрэнгүй" position="bottom">
              <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("detail", record)}>
                <IconInfoSquare />
              </Button>
            </Tooltip>
          )}

          {(user?.permissions || []).filter((c: IPermissions) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_SL" && c.isReview).length > 0 &&
            record.registrationStatus === "REGISTERED" &&
            record.effectiveFrom === null &&
            record.productStatus === "INACTIVE" && (
              <Tooltip label="Хянах" position="bottom">
                <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("control", record)}>
                  <IconEyeCheck />
                </Button>
              </Tooltip>
            )}
        </Flex>
      );
    },
  },
  {
    title: "#",
    render: (_r, index) => `${index + 1}`,
    width: 40,
  },
  {
    title: "SCF код",
    sorter: true,
    dataIndex: "refCode",
    render: (record) => {
      return (
        <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
          {record?.refCode || "-"}
        </Text>
      );
    },
  },
  {
    title: "Бүтээгдэхүүний нэр",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return (
        <Text style={{ width: 250 }} size="sm" weight={500} c="#44566C">
          {record.name || "-"}
        </Text>
      );
    },
  },
  {
    title: "Статус",
    sorter: true,
    dataIndex: "productStatus",
    render: (record) => {
      return (
        <Badge size="md" radius="sm" variant="outline" color={(productStatus || []).find((item: IReference) => item.code === record.productStatus)?.color}>
          {(productStatus || []).find((item: IReference) => item.code === record.productStatus)?.name}
        </Badge>
      );
    },
  },
  {
    title: "Төрөл",
    sorter: true,
    dataIndex: "type",
    render: (record) => {
      return (
        <Badge size="md" radius="sm" variant="outline">
          {(productTypes || []).find((item: IReference) => item.code === record.type)?.name}
        </Badge>
      );
    },
  },
  {
    title: "Ангилал",
    sorter: true,
    dataIndex: "category",
    render: (record) => {
      return (
        <Text style={{ width: 150 }} size="sm" weight={500} c="#44566C">
          {(productCategories || []).find((item: IReference) => item.code === record.category)?.name}
        </Text>
      );
    },
  },
  {
    title: "Буцаан дуудах",
    sorter: true,
    dataIndex: "recourseTerm",
    render: (record) => {
      return (
        <Checkbox
          checked={record.recourseTerm}
          label={
            record.recourseTerm ? (
              <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
                {"Тийм"}
              </Text>
            ) : (
              <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
                {"Үгүй"}
              </Text>
            )
          }
          onChange={() => {}}
        />
      );
    },
  },
  {
    title: "Supplier санхүүжих",
    sorter: true,
    dataIndex: "borrowerType",
    render: (record) => {
      return (
        <Checkbox
          checked={record.borrowerType === "SUPPLIER_ONLY" || record.borrowerType === "BUYER"}
          label={
            record.borrowerType === "SUPPLIER_ONLY" || record.borrowerType === "BUYER" ? (
              <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
                {"Тийм"}
              </Text>
            ) : (
              <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
                {"Үгүй"}
              </Text>
            )
          }
          onChange={() => {}}
        />
      );
    },
  },
  {
    title: "Buyer санхүүжих",
    sorter: true,
    dataIndex: "borrowerType",
    render: (record) => {
      return (
        <Checkbox
          checked={record.borrowerType === "BUYER"}
          label={
            record.borrowerType === "BUYER" ? (
              <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
                {"Тийм"}
              </Text>
            ) : (
              <Text style={{ width: 100 }} size="sm" weight={500} c="#44566C">
                {"Үгүй"}
              </Text>
            )
          }
          onChange={() => {}}
        />
      );
    },
  },
  {
    title: "Бүртгэл",
    sorter: true,
    dataIndex: "registrationStatus",
    render: (record) => {
      return (
        <Badge
          size="md"
          radius="sm"
          variant="outline"
          color={(productRegistrationStatus || []).find((item: IReference) => item.code === record.registrationStatus)?.color}>
          {(productRegistrationStatus || []).find((item: IReference) => item.code === record.registrationStatus)?.name}
        </Badge>
      );
    },
  },
  {
    title: "Бүртгэсэн",
    sorter: true,
    dataIndex: "createdAt",
    render: (record) => {
      return (
        <Text style={{ width: 150 }} size="sm" weight={500} c="#44566C">
          {dateSecFormat(record.createdAt)}
        </Text>
      );
    },
  },
  {
    title: "Бүртгэсэн ажилтан",
    sorter: true,
    dataIndex: "quantity",
    render: (record) => {
      return (
        <Group noWrap>
          <Avatar src={record?.regUser?.avatar !== null && record?.regUser !== null ? record?.regUser?.avatar : noimage} />
          <div>
            <Text size="sm" weight={500} c="#44566C">
              {record?.regUser?.lastName}
            </Text>
            <Flex gap={5} wrap="nowrap">
              <Text size="sm" weight={500} c="#141517">
                {record?.regUser?.firstName}
              </Text>
            </Flex>
          </div>
        </Group>
      );
    },
  },
  {
    title: "Хянасан огноо",
    sorter: true,
    dataIndex: "reviewedDate",
    render: (record) => {
      return (
        <Text style={{ width: 150 }} size="sm" weight={500} c="#44566C">
          {dateSecFormat(record.reviewedDate)}
        </Text>
      );
    },
  },
  {
    title: "Хянасан",
    sorter: true,
    dataIndex: "quantity",
    render: (record) => {
      return (
        <Group noWrap>
          <Avatar src={record?.reviewedUser?.avatar !== null && record?.reviewedUser !== null ? record?.reviewedUser?.avatar : noimage} />
          <div>
            <Text size="sm" weight={500} c="#44566C">
              {record?.reviewedUser?.lastName || "-"}
            </Text>
            <Flex gap={5} wrap="nowrap">
              <Text size="sm" weight={500} c="#141517">
                {record?.reviewedUser?.firstName || "-"}
              </Text>
            </Flex>
          </div>
        </Group>
      );
    },
  },
];

const useBreadcrumb = () => [
  {
    to: "/",
    label: "Үндсэн",
  },
  {
    label: "Санхүүжилтийн бүтээгдэхүүн",
  },
  {
    label: "Supplier LED бүтээгдэхүүн",
  },
];
